"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqs = [{
  id: "item-1",
  question: "What is included in a typical month?",
  answer: "Every month begins with prioritized deliverables based on your search performance and business goals. I handle hands-on technical audits, on-page optimization, content briefs, schema updates, and backlink analysis myself. You receive clear visibility into exactly what I worked on and what comes next."
}, {
  id: "item-2",
  question: "Is there a long-term lock-in contract?",
  answer: "No. I work on a rolling monthly basis with a simple 30-day notice period. I believe ongoing partnerships should be earned through quality work and transparent communication, not restrictive multi-year contracts."
}, {
  id: "item-3",
  question: "How often do I receive reports and updates?",
  answer: "You receive a comprehensive monthly video walkthrough and executive summary detailing ranking trends, organic clicks, conversions, and completed tasks. We also have a monthly review call to discuss strategic priorities and upcoming search opportunities."
}, {
  id: "item-4",
  question: "How are results measured and tracked?",
  answer: "I track qualified organic traffic, non-brand search impressions, assisted conversions, and commercial keyword positions using Google Search Console and Google Analytics 4. I focus on metrics that contribute directly to your business revenue, rather than vanity numbers."
}, {
  id: "item-5",
  question: "How does monthly retainer pricing work?",
  answer: "My retainers are tailored to the scope of your website, market competition, and technical complexity. I offer fixed monthly tiers so you have complete cost predictability with zero hidden fees or unexpected billable hours."
}, {
  id: "item-6",
  question: "How is this different from a one-off SEO project?",
  answer: "A one-off audit identifies technical issues and foundational gaps, while a monthly retainer provides continuous strategic execution and iterative optimization. Search algorithms, competitors, and indexing behavior constantly evolve, making ongoing attention essential for sustained growth."
}, {
  id: "item-7",
  question: "What is the recommended minimum commitment?",
  answer: "While agreements are month-to-month, I typically recommend planning for at least 3 to 6 months. Search engines require time to crawl, index, and re-evaluate changes before organic visibility and qualified traffic reliably reflect our work."
}, {
  id: "item-8",
  question: "How do monthly retainer sprints and collaboration work?",
  answer: "Each monthly cycle begins with a prioritized task backlog tailored to your search goals. You collaborate directly with me throughout the month on implementation, concluding with a full video review and roadmap alignment for the next cycle."
}, {
  id: "item-9",
  question: "Can I pause or cancel my retainer?",
  answer: "Yes, you can pause or cancel anytime with 30 days of written notice. All assets, documentation, schemas, and technical recommendations implemented during our time together remain entirely your property."
}, {
  id: "item-10",
  question: "What if I don't see instant results?",
  answer: "SEO is a compounding process rather than an overnight switch, and I never offer dishonest ranking guarantees. If progress slows, I run deeper competitor and technical diagnostics to pivot our priority list and maintain momentum toward your business targets."
}];
export default function RetainerFaq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="monthlyretainerfaq" data-nav="light" className="bg-background py-20 md:py-28 text-foreground border-t border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Heading & Context */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
            <div className="space-y-3">
              <Badge variant="secondary" className="gap-1.5 px-3 py-1 font-medium text-xs">
                <HelpCircle className="size-3.5 text-primary" />
                <span>Monthly retainer questions</span>
              </Badge>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground font-serif">
                Clear answers before we work together.
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed">
                No jargon, no arbitrary lock-ins, and no inflated promises. Here is exactly how I run monthly SEO consultancy partnerships.
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl p-6 space-y-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <ShieldCheck className="size-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground font-default">
                    The consultant guarantee
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Structured monthly sprints &amp; direct execution
                  </p>
                </div>
              </div>

              <ul className="space-y-2.5 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>Work exclusively with Yassine Essaadi</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>No long-term contracts or hidden retainers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>Clear monthly roadmap and recorded video reviews</span>
                </li>
              </ul>

              <div className="pt-2 border-t border-border/60">
                <p className="text-xs text-muted-foreground mb-3">
                  Have a specific question about your site?
                </p>
                <Button asChild className="w-full justify-between" size="default">
                  <Link to="/ContactPage">
                    <span>Ask a specific question</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Accordion Items */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-7">
            <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-8 shadow-xs">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-2">
                {faqs.map((faq, index) => <div key={faq.id} data-index={index}>
                    <AccordionItem value={faq.id} className="border border-border/80 bg-card rounded-xl px-5 transition-[border-color,box-shadow] data-[state=open]:border-primary/40 data-[state=open]:shadow-xs">
                      <AccordionTrigger className="text-left font-default font-semibold text-foreground hover:no-underline py-4.5 gap-4">
                        <span className="text-base leading-snug">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm leading-relaxed pb-5 pt-1 border-t border-border/40 mt-1">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </div>)}
              </Accordion>
            </div>

            {/* Bottom Support Banner */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 p-5 rounded-xl bg-secondary/40 border border-secondary text-secondary-foreground text-sm">
              <div className="flex items-center gap-3">
                <MessageSquare className="size-5 text-primary shrink-0" />
                <span>Need an SEO audit before starting a monthly retainer?</span>
              </div>
              <Button asChild variant="outline" size="sm" className="shrink-0 bg-background">
                <Link to="/SeoAuditsPage">Explore SEO audits</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}